import type { CameraRoute } from '@datum-sdk/scene-format';

/**
 * The Arafat preloader flights, as SDK scene data: the tour, then a flyover
 * that climbs over Jabal al-Rahma, rings it once and descends onto the rest
 * pose — its last view.
 *
 * Converted once from the legacy `arafatSceneConfig.camera` by
 * `convertLegacyPreloader.ts` (which says what in here is load-bearing); the
 * routes are the source since. Tune them in the SDK editor (`?flight` → Routes →
 * Copy Routes) — the same workflow as `kaabaRoutes.ts` — and
 * `sceneRoutes.test.ts` holds them to what the preloader and the scene need.
 *
 * The full turn is 18 views, one every 20°: a classic orbit waypoint cannot
 * sweep 360° (CR C13), a smooth run can — at about a frame per knot (CR C14).
 */
export const ARAFAT_CAMERA_ROUTES: CameraRoute[] = [
  {
    id: 'preview-enter',
    waypoints: [
      {
        target: [-0.8, -0.08, 3.21],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 1000,
        easing: [0.65, 0, 0.35, 1],
      },
      {
        orbit: {
          radius: 3.34572862,
          azimuth: 1.993928844,
          polar: 1.459980897,
        },
        target: [-0.01, -0.45, -0.02],
        lookAt: 'target',
        duration: 500,
        easing: [0.333333333, 0, 0.666666667, 0.333333333],
      },
    ],
  },
  {
    id: 'preview-turntable',
    loop: true,
    waypoints: [
      {
        orbit: {
          radius: 3.34572862,
          azimuth: 3.56472517,
          polar: 1.459980897,
        },
        target: [-0.01, -0.45, -0.02],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 3.34572862,
          azimuth: 5.135521497,
          polar: 1.459980897,
        },
        target: [-0.01, -0.45, -0.02],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 3.34572862,
          azimuth: 6.706317824,
          polar: 1.459980897,
        },
        target: [-0.01, -0.45, -0.02],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 3.34572862,
          azimuth: 8.277114151,
          polar: 1.459980897,
        },
        target: [-0.01, -0.45, -0.02],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
    ],
  },
  {
    id: 'preview-exit',
    waypoints: [
      {
        target: [0.38, -0.9, 3.54],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 1000,
        easing: [0.65, 0, 0.35, 1],
      },
    ],
  },
  {
    id: 'preloader-flyover',
    waypoints: [
      {
        duration: 800,
      },
      {
        kind: 'view',
        position: [0.3, 0.55, 3.57],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 1000,
        easing: [0.333333333, 0, 0.666666667, 0.224103939],
      },
      {
        kind: 'view',
        position: [-0.946454153, 0.55, 3.459175566],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 365.189670186,
        easing: [0.333333333, 0.327531893, 0.666666667, 0.661600964],
      },
      {
        kind: 'view',
        position: [-2.079927183, 0.55, 2.929069422],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 348.196453962,
        easing: [0.333333333, 0.328821344, 0.666666667, 0.662396993],
      },
      {
        kind: 'view',
        position: [-2.963736446, 0.55, 2.043261982],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 339.969540263,
        easing: [0.333333333, 0.329625137, 0.666666667, 0.663186332],
      },
      {
        kind: 'view',
        position: [-3.491281501, 0.55, 0.908594697],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 333.419616153,
        easing: [0.333333333, 0.330324546, 0.666666667, 0.663874696],
      },
      {
        kind: 'view',
        position: [-3.598932629, 0.55, -0.338074814],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 328.283721778,
        easing: [0.333333333, 0.330947732, 0.666666667, 0.66448952],
      },
      {
        kind: 'view',
        position: [-3.273705514, 0.55, -1.546379807],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 324.374832284,
        easing: [0.333333333, 0.331515541, 0.666666667, 0.665051138],
      },
      {
        kind: 'view',
        position: [-2.554827348, 0.55, -2.570580869],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 321.561759445,
        easing: [0.333333333, 0.332044088, 0.666666667, 0.665575321],
      },
      {
        kind: 'view',
        position: [-1.529005445, 0.55, -3.287144236],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 319.755767445,
        easing: [0.333333333, 0.332546445, 0.666666667, 0.666074916],
      },
      {
        kind: 'view',
        position: [-0.319969068, 0.55, -3.609641789],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 318.902301986,
        easing: [0.333333333, 0.333033799, 0.666666667, 0.666560975],
      },
      {
        kind: 'view',
        position: [0.926454153, 0.55, -3.499175566],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 318.976223631,
        easing: [0.333333333, 0.333516316, 0.666666667, 0.667043604],
      },
      {
        kind: 'view',
        position: [2.059927183, 0.55, -2.969069422],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 319.97969369,
        easing: [0.333333333, 0.334003846, 0.666666667, 0.667532658],
      },
      {
        kind: 'view',
        position: [2.943736446, 0.55, -2.083261982],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 321.942353921,
        easing: [0.333333333, 0.334506598, 0.666666667, 0.668038417],
      },
      {
        kind: 'view',
        position: [3.471281501, 0.55, -0.948594697],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 324.923830072,
        easing: [0.333333333, 0.335035846, 0.666666667, 0.668572305],
      },
      {
        kind: 'view',
        position: [3.578932629, 0.55, 0.298074814],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 329.018986575,
        easing: [0.333333333, 0.335604823, 0.666666667, 0.669147793],
      },
      {
        kind: 'view',
        position: [3.253705514, 0.55, 1.506379807],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 334.366882103,
        easing: [0.333333333, 0.336229899, 0.666666667, 0.669781622],
      },
      {
        kind: 'view',
        position: [2.534827348, 0.55, 2.530580869],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 341.165197245,
        easing: [0.333333333, 0.336932347, 0.666666667, 0.670495605],
      },
      {
        kind: 'view',
        position: [1.509005445, 0.55, 3.247144236],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 349.693349673,
        easing: [0.333333333, 0.337741061, 0.666666667, 0.671319455],
      },
      {
        kind: 'view',
        position: [0.299969068, 0.55, 3.569641789],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 360.279819589,
        easing: [0.333333333, 0.338658011, 0.666666667, 0.672331084],
      },
      {
        kind: 'view',
        position: [-0.801, 0.012, 4.146],
        lookAt: [-0.01, -0.45, -0.02],
        duration: 1000,
        easing: [0.333333333, 0.841716812, 0.666666667, 1],
      },
    ],
  },
];
